import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { log } from '../config/env';

interface AuthGuardProps {
  children: React.ReactNode;
}

// Define auth routes that don't require authentication
const AUTH_ROUTES = [
  '/auth/login',
  '/auth/signup',
  '/auth/forgotpassword',
  '/auth/confirmforgottenpassword',
  '/auth/confirmotp',
];

// Check if current path is an auth route
const isAuthRoute = (pathname: string): boolean => {
  return AUTH_ROUTES.some(route => pathname.startsWith(route));
};

const AuthGuard: React.FC<AuthGuardProps> = React.memo(({ children }) => {
  console.log('🔄 AuthGuard component rendering');
  const { isAuthenticated, token, checkAuthorization, logout, loading, justLoggedIn, clearJustLoggedIn } = useAuth();
  const [authChecking, setAuthChecking] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false); // Track if initial authorization check was done
  const location = useLocation();
  const navigate = useNavigate();

  // Memoize the current path check to prevent unnecessary re-renders
  const isCurrentPathAuthRoute = useMemo(() => {
    console.log('🔄 AuthGuard path check', { pathname: location.pathname, isAuthRoute: isAuthRoute(location.pathname) });
    return isAuthRoute(location.pathname);
  }, [location.pathname]);

  // Memoize the auth check function to prevent recreation
  const performAuthCheck = useCallback(async () => {
    // Skip auth check for auth routes
    if (isCurrentPathAuthRoute) {
      log.debug('Skipping auth check for auth route:', location.pathname);
      setAuthCheckComplete(true);
      setInitialCheckDone(true);
      return;
    }

    // If we're already loading from AuthContext, wait for it to complete
    if (loading) {
      log.debug('AuthContext is still loading, waiting...');
      return;
    }

    // If no token, redirect to login immediately
    if (!token && !localStorage.getItem('token')) {
      log.debug('No token found, redirecting to login');
      navigate('/auth/login', { replace: true });
      return;
    }

    // If not authenticated according to context, redirect to login
    if (!isAuthenticated) {
      log.debug('User not authenticated according to context, redirecting to login');
      navigate('/auth/login', { replace: true });
      return;
    }

    // If we reach here, user is authenticated - allow access
    log.debug('User is authenticated, allowing access');
    setAuthCheckComplete(true);
    setInitialCheckDone(true);
  }, [isCurrentPathAuthRoute, location.pathname, isAuthenticated, token, loading, navigate]);

  useEffect(() => {
    performAuthCheck();
  }, [performAuthCheck]);

  // Reset initial check when user logs out
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      setInitialCheckDone(false);
      setAuthCheckComplete(false);
    }
  }, [isAuthenticated, loading]);

  // Show loading while AuthContext is initializing
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show loading while performing auth check
  if (authChecking) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verifying authentication...</span>
        </div>
      </div>
    );
  }

  // For auth routes, always render children
  if (isCurrentPathAuthRoute) {
    return <>{children}</>;
  }

  // For protected routes, only render if auth check is complete
  if (authCheckComplete) {
    return <>{children}</>;
  }

  // Default loading state
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
});

AuthGuard.displayName = 'AuthGuard';
export default AuthGuard;
